import React from 'react'
import validator from 'validator'
import { compose } from 'redux'
import { connect } from 'react-redux'
import { createStructuredSelector } from 'reselect'
import { firebaseConnect } from 'react-redux-firebase'
import { reduxForm, Field, SubmissionError } from 'redux-form'
import { defineMessages, intlShape } from 'react-intl'
import { isValidAddress, isValidChecksumAddress } from 'ethereumjs-util'

import * as commands from '../commands'
import * as UI from '../components'
import * as constants from '../constants'
import { userIsAuthenticated } from '../behaviors'
import { dataToArray } from '../helpers'

const walletOptions = constants.walletOptions

const messages = defineMessages({
  header: {
    id: 'sign-up.header',
    defaultMessage:
      'Registering will speed up your purchase process on the sale day. Do it.',
  },
  emailLabel: { id: 'sign-up.email.label', defaultMessage: 'E-mail Address' },
  emailPlaceholder: {
    id: 'sign-up.email.placeholder',
    defaultMessage: 'Your e-mail address',
  },
  passwordLabel: { id: 'sign-up.password.label', defaultMessage: 'Password' },
  passwordPlaceholder: {
    id: 'sign-up.password.placeholder',
    defaultMessage: 'Make it a good one',
  },
  returnAddressHint: {
    id: 'sign-up.returnAddress.hint',
    defaultMessage: 'You can provide this later',
  },
  returnAddressLabel: {
    id: 'sign-up.returnAddress.label',
    defaultMessage: 'Ethereum Account to Deliver Tokens',
  },
  returnAddressPlaceholder: {
    id: 'sign-up.returnAddress.placeholder',
    defaultMessage: 'Your address',
  },
  walletLabel: { id: 'sign-up.wallet.label', defaultMessage: 'Wallet' },
  walletPlaceholder: {
    id: 'sign-up.wallet.placeholder',
    defaultMessage: 'Select one...',
  },
  walletHint: {
    id: 'sign-up.wallet.hint',
    defaultMessage:
      'Incompatible: Exodus, Coinbase. Poloniex, Kraken, or any exchange.',
  },
  referralCode: {
    id: 'log-in.referral-code',
    defaultMessage: 'Invitation Code',
  },
  referralCodePlaceholder: {
    id: 'sign-up.referral-code.placeholder',
    defaultMessage: 'Optional',
  },
  submit: { id: 'sign-up.submit', defaultMessage: 'Register' },
  termsAndConditions: {
    id: 'sign-up.terms-and-conditions',
    defaultMessage:
      'By clicking register, you agree to our Terms and Conditions',
  },
})

class Settings extends React.Component {
  render() {
    const {
      addReferralCode,
      error,
      handleSubmit,
      invalid,
      pristine,
      storefront,
      submitFailed,
      submitSucceeded,
      submitting,
      trackExternal,
      types,
      updateProfile,
    } = this.props

    const { intl } = this.context

    return (
      <div>
        <UI.Form size="large" onSubmit={handleSubmit(updateProfile)}>
          <UI.Card loading={submitting}>
            <UI.Text uppercase color="accent">
              Update your profile
            </UI.Text>

            <UI.Divider />

            {submitFailed && <UI.Message type="error">{error}</UI.Message>}
            {submitSucceeded && (
              <UI.Message type="success">
                Your profile has been updated
              </UI.Message>
            )}

            <UI.Divider hidden />

            <Field
              fluid
              label="Display Name"
              name="displayName"
              component={UI.TextField}
            />

            <Field
              fluid
              icon="user"
              iconPosition="left"
              label="E-mail Address"
              name="email"
              component={UI.TextField}
            />

            <Field
              fluid
              icon="twitter"
              iconPosition="left"
              label="Twitter Handle"
              name="twitter"
              type="text"
              hint={
                <span>
                  Follow us on{' '}
                  <UI.Link to={storefront.twitterLink} target="blank" onClick={trackExternal('twitter')}>
                    Twitter {storefront.twitterHandle}
                  </UI.Link>
                </span>
              }
              component={UI.TextField}
            />

            <Field
              fluid
              icon="telegram"
              iconPosition="left"
              label="Telegram Username"
              name="telegram"
              type="text"
              hint={
                <span>
                  Join our{' '}
                  <UI.Link to={storefront.telegramLink} target="blank" onClick={trackExternal('telegram')}>
                    Telegram group
                  </UI.Link>
                </span>
              }
              component={UI.TextField}
            />

            <UI.Divider hidden />

            <Field
              fluid
              label={intl.formatMessage(messages.returnAddressLabel)}
              placeholder={intl.formatMessage(
                messages.returnAddressPlaceholder
              )}
              name="destinationAddress"
              component={UI.TextField}
            />
            <Field
              fluid
              label="Wallet"
              placeholder="Select one..."
              name="wallet"
              component={UI.SelectField}
              options={walletOptions}
            />

            <UI.Divider hidden />

            <UI.Button disabled={pristine || invalid || submitting}>
              Save
            </UI.Button>
          </UI.Card>
        </UI.Form>

        <UI.Form size="large" onSubmit={handleSubmit(addReferralCode)}>
          <UI.Card>
            <UI.Text uppercase color="accent">
              Invitation Codes
            </UI.Text>

            <UI.Divider />
            <UI.Divider hidden />

            <Field
              fluid
              label="Add Invitation Code"
              placeholder="Enter code"
              name="referralCode"
              component={UI.TextField}
            />

            {dataToArray(types).map((type, i) => (
              <UI.Label basic key={i} color="orange" size="large">
                {type.name}
              </UI.Label>
            ))}

            <UI.Divider hidden />
            <UI.Button disabled={pristine || invalid || submitting}>
              Submit
            </UI.Button>
          </UI.Card>
        </UI.Form>
      </div>
    )
  }
}

Settings.contextTypes = {
  intl: intlShape,
}

const selectors = createStructuredSelector({
  initialValues: ({ firebase }) => ({
    destinationAddress: firebase.profile.destinationAddress || '',
    displayName: firebase.profile.displayName || '',
    email: firebase.auth.email || '',
    wallet: firebase.profile.wallet || '',
  }),
  types: ({ firebase }) => firebase.data.userTypes,
})

const actions = (dispatch, props) => ({
  trackExternal: (channel) => async () => {
    dispatch({
      type: `CLICKED_${channel.toUpperCase()}_EXTERNAL_LINK`,
      track: {},
    })
  },

  updateProfile: async ({ email, ...profile }) => {
    try {
      return await Promise.all([
        props.firebase.updateProfile(profile),
        props.firebase.updateEmail(email, true),
      ])
    } catch (error) {
      throw new SubmissionError({ _error: error.message })
    }
  },

  addReferralCode: async values => {
    try {
      const authToken = await props.firebase.auth().currentUser.getIdToken(true)
      await commands.redeemCode(authToken, values)
      dispatch({ type: 'ADD_REFERRAL_CODE', track: {} })
    } catch (error) {
      throw new SubmissionError({ _error: error.message })
    }
  },
})

const validate = values => {
  const errors = {}

  if (validator.isEmpty(values.email))
    errors.email = 'E-mail address is required'
  if (
    !validator.isEmpty(values.destinationAddress) &&
    !isValidAddress(values.destinationAddress)
  )
    errors.destinationAddress = 'This is an invalid Ethereum Address'

  return errors
}

const warn = values => {
  const warn = {}

  if (
    !validator.isEmpty(values.destinationAddress) &&
    isValidAddress(values.destinationAddress) &&
    !isValidChecksumAddress(values.destinationAddress)
  )
    warn.destinationAddress =
      'This is an invalid (EIP-55) Ethereum Address - please double-check'

  return warn
}

export default compose(
  firebaseConnect(),
  connect(selectors, actions),
  reduxForm({ form: 'Settings', validate, warn, enableReinitialize: true }),
  userIsAuthenticated()
)(Settings)



// WEBPACK FOOTER //
// ./src/screens/Settings.js